import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Alert,
  Chip
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface CustomCSSEditorProps {
  css: string;
  onChange: (css: string) => void;
}

interface ValidationResult {
  isValid: boolean;
  warnings: string[];
  errors: string[];
}

const DANGEROUS_PATTERNS = [
  { pattern: /<script[\s\S]*?<\/script>/gi, message: 'Script tags are not allowed' },
  { pattern: /javascript:/gi, message: 'JavaScript URLs are not allowed' },
  { pattern: /on\w+\s*=/gi, message: 'Event handlers are not allowed' },
  { pattern: /@import\s+/gi, message: '@import rules are not allowed for security' },
  { pattern: /expression\s*\(/gi, message: 'CSS expressions are not allowed' },
  { pattern: /behavior\s*:/gi, message: 'CSS behaviors are not allowed' },
  { pattern: /-moz-binding\s*:/gi, message: 'XBL bindings are not allowed' },
  { pattern: /vbscript:/gi, message: 'VBScript URLs are not allowed' },
  { pattern: /data:text\/html/gi, message: 'data:text/html URLs are not allowed' }
];

const RISKY_PATTERNS = [
  { pattern: /url\s*\(\s*["']?(?:https?:)?\/\//gi, message: 'External URLs may cause security issues' },
  { pattern: /position\s*:\s*fixed/gi, message: 'Fixed positioning may interfere with layout' },
  { pattern: /z-index\s*:\s*\d{4,}/gi, message: 'Very high z-index values may cause layering issues' }
];

export const CustomCSSEditor: React.FC<CustomCSSEditorProps> = ({ css, onChange }) => {
  const [validation, setValidation] = useState<ValidationResult>({
    isValid: true,
    warnings: [],
    errors: []
  });

  useEffect(() => {
    validateCSS(css);
  }, [css]);

  const validateCSS = (cssCode: string) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Check for dangerous patterns
    DANGEROUS_PATTERNS.forEach(({ pattern, message }) => {
      if (pattern.test(cssCode)) {
        errors.push(message);
      }
    });

    // Check for risky patterns
    RISKY_PATTERNS.forEach(({ pattern, message }) => {
      if (pattern.test(cssCode)) {
        warnings.push(message);
      }
    });

    // Basic CSS syntax validation
    const braceCount = (cssCode.match(/{/g) || []).length - (cssCode.match(/}/g) || []).length;
    if (braceCount !== 0) {
      errors.push('Unmatched braces in CSS');
    }

    // Check for empty selectors
    if (/{\s*}/g.test(cssCode)) {
      warnings.push('Empty CSS rules detected');
    }

    setValidation({
      isValid: errors.length === 0,
      warnings,
      errors
    });
  };

  const sanitizeCSS = (cssCode: string): string => {
    let sanitized = cssCode;

    // Remove dangerous patterns
    DANGEROUS_PATTERNS.forEach(({ pattern }) => {
      sanitized = sanitized.replace(pattern, '/* REMOVED FOR SECURITY */');
    });

    return sanitized;
  };

  const handleChange = (value: string) => {
    // Sanitize before passing to parent
    const sanitized = sanitizeCSS(value);
    onChange(sanitized);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Custom CSS
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add custom CSS to further customize your site's appearance. Use with caution.
      </Typography>

      {validation.errors.length > 0 && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Security Issues Detected:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {validation.warnings.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Warnings:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {validation.isValid && css.length > 0 && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
          CSS looks good! No security issues detected.
        </Alert>
      )}

      <Paper sx={{ p: 0, mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={20}
          value={css}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="/* Enter your custom CSS here */&#10;&#10;.custom-class {&#10;  color: #333;&#10;  font-size: 16px;&#10;}"
          sx={{
            '& .MuiInputBase-root': {
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: 1.5
            },
            '& textarea': {
              padding: 2
            }
          }}
          InputProps={{
            sx: {
              backgroundColor: '#f5f5f5'
            }
          }}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          CSS Guidelines & Best Practices
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="success.main" gutterBottom>
            ✓ Allowed:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label="Colors" size="small" />
            <Chip label="Fonts" size="small" />
            <Chip label="Spacing" size="small" />
            <Chip label="Borders" size="small" />
            <Chip label="Backgrounds" size="small" />
            <Chip label="Animations" size="small" />
            <Chip label="Transitions" size="small" />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="error.main" gutterBottom>
            ✗ Not Allowed:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label="<script> tags" size="small" color="error" />
            <Chip label="javascript: URLs" size="small" color="error" />
            <Chip label="Event handlers" size="small" color="error" />
            <Chip label="CSS expressions" size="small" color="error" />
          </Box>
        </Box>

        <Typography variant="body2" component="div">
          <strong>Tips:</strong>
          <ul>
            <li>Use specific class names to avoid conflicts (e.g., .my-custom-header)</li>
            <li>Test your CSS in the preview before publishing</li>
            <li>Avoid using !important unless absolutely necessary</li>
            <li>Keep your CSS organized with comments</li>
            <li>External resources via @import or url() may be blocked for security</li>
          </ul>
        </Typography>

        <Typography variant="body2" component="div" sx={{ mt: 2 }}>
          <strong>Example:</strong>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '12px',
              fontFamily: 'monospace'
            }}
          >
{`/* Custom header styling */
.custom-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  border-radius: 8px;
}

/* Custom button */
.custom-button {
  background-color: #4CAF50;
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.custom-button:hover {
  background-color: #45a049;
  transform: translateY(-2px);
}`}
          </Box>
        </Typography>
      </Paper>
    </Box>
  );
};
